export const RESTORE_HEALTH_FLAT = 'RestoreHealthFlat';
export const RESTORE_SATIETY_FLAT = 'RestoreSatietyFlat';
export const TEMPORARY_STAT_FLAT = 'TemporaryStatFlat';
export const TEMPORARY_ATTRIBUTE_FLAT = 'TemporaryAttributeFlat';

const TIMED_EFFECT_TYPES = new Set([
    TEMPORARY_STAT_FLAT,
    TEMPORARY_ATTRIBUTE_FLAT,
]);

const FALLBACK_STAT_TARGETS = {
    health: { displayName: 'maximum HP', unit: '' },
    damage: { displayName: 'Damage', unit: '' },
    armor: { displayName: 'Armor', unit: '' },
    hpregenperminute: { displayName: 'HP regeneration/min', unit: '' },
    hpregenpercent: { displayName: 'HP regeneration', unit: '%' },
    weaponattackspeed: { displayName: 'attack speed', unit: ' APM' },
    critchance: { displayName: 'Critical chance', unit: '%' },
    critdamage: { displayName: 'Critical damage', unit: '%' },
    dodge: { displayName: 'Dodge', unit: '%' },
    flee: { displayName: 'Flee', unit: '' },
    adrenaline: { displayName: 'Adrenaline', unit: '' },
    magicfind: { displayName: 'magic find', unit: ' MF' },
};

const FALLBACK_ATTRIBUTE_TARGETS = [
    { attributeKey: 'Strength', displayName: 'Strength' },
    { attributeKey: 'Vitality', displayName: 'Vitality' },
    { attributeKey: 'Resilience', displayName: 'Resilience' },
];

const read = (value, camelCase, pascalCase) => (
    value?.[camelCase] ?? value?.[pascalCase]
);

const normalizeName = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const formatNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric)
        ? Number(numeric.toFixed(2)).toString()
        : String(value ?? '');
};

export function getConsumableEffects(item) {
    const effects = read(item, 'consumableEffects', 'ConsumableEffects');
    return Array.isArray(effects) ? effects : [];
}

export function getConsumableDurationSeconds(item) {
    return read(item, 'consumableDurationSeconds', 'ConsumableDurationSeconds');
}

export function getConsumableEffectType(effect) {
    return read(effect, 'type', 'Type') || '';
}

export function isTimedConsumableEffect(effect) {
    return TIMED_EFFECT_TYPES.has(getConsumableEffectType(effect));
}

export function getConsumableTargetValue(effect) {
    const type = getConsumableEffectType(effect);
    if (type === RESTORE_HEALTH_FLAT) {
        return `${RESTORE_HEALTH_FLAT}:Health`;
    }
    if (type === RESTORE_SATIETY_FLAT) {
        return `${RESTORE_SATIETY_FLAT}:Satiety`;
    }
    if (type === TEMPORARY_STAT_FLAT) {
        return `${TEMPORARY_STAT_FLAT}:${read(effect, 'statId', 'StatId') || ''}`;
    }
    if (type === TEMPORARY_ATTRIBUTE_FLAT) {
        return `${TEMPORARY_ATTRIBUTE_FLAT}:${read(effect, 'attributeKey', 'AttributeKey') || ''}`;
    }
    return `${type}:`;
}

function normalizeTarget(target) {
    const effectType = read(target, 'effectType', 'EffectType');
    const statId = read(target, 'statId', 'StatId');
    const attributeKey = read(target, 'attributeKey', 'AttributeKey');
    const targetKey = read(target, 'targetKey', 'TargetKey')
        || statId
        || attributeKey
        || (effectType === RESTORE_HEALTH_FLAT
            ? 'Health'
            : effectType === RESTORE_SATIETY_FLAT
                ? 'Satiety'
                : '');

    return {
        effectType,
        targetKey,
        statId,
        attributeKey,
        displayName: read(target, 'displayName', 'DisplayName') || targetKey,
        unit: read(target, 'unit', 'Unit') || '',
        inputStep: Number(read(target, 'inputStep', 'InputStep')) || 1,
        runtimeScale: Number(read(target, 'runtimeScale', 'RuntimeScale')) || 1,
        value: `${effectType}:${statId || attributeKey || targetKey}`,
    };
}

export function buildConsumableEffectTargets(catalog, stats = []) {
    if (Array.isArray(catalog) && catalog.length > 0) {
        return catalog
            .map(normalizeTarget)
            .filter(target => (
                target.effectType === RESTORE_HEALTH_FLAT
                || target.effectType === RESTORE_SATIETY_FLAT
                || target.effectType === TEMPORARY_STAT_FLAT
                || target.effectType === TEMPORARY_ATTRIBUTE_FLAT
            ));
    }

    const statTargets = (stats || []).flatMap((stat) => {
        const fallback = FALLBACK_STAT_TARGETS[normalizeName(read(stat, 'name', 'Name'))];
        const statId = read(stat, 'id', 'Id');
        if (!fallback || !statId) {
            return [];
        }
        return [normalizeTarget({
            effectType: TEMPORARY_STAT_FLAT,
            targetKey: read(stat, 'name', 'Name'),
            statId,
            displayName: fallback.displayName,
            unit: read(stat, 'unitSymbol', 'UnitSymbol') || fallback.unit,
            inputStep: 0.01,
            runtimeScale: ['critchance', 'critdamage', 'dodge']
                .includes(normalizeName(read(stat, 'name', 'Name'))) ? 0.01 : 1,
        })];
    });

    return [
        normalizeTarget({
            effectType: RESTORE_HEALTH_FLAT,
            targetKey: 'Health',
            displayName: 'Restore health',
            unit: 'HP',
            inputStep: 1,
        }),
        normalizeTarget({
            effectType: RESTORE_SATIETY_FLAT,
            targetKey: 'Satiety',
            displayName: 'Restore satiety',
            unit: '%',
            inputStep: 1,
        }),
        ...statTargets,
        ...FALLBACK_ATTRIBUTE_TARGETS.map(target => normalizeTarget({
            ...target,
            effectType: TEMPORARY_ATTRIBUTE_FLAT,
            targetKey: target.attributeKey,
            inputStep: 1,
        })),
    ];
}

export function findConsumableEffectTarget(effect, targets) {
    const value = getConsumableTargetValue(effect);
    return (targets || []).find(target => target.value === value);
}

export function createConsumableEffect(target, amount = 1) {
    if (target.effectType === TEMPORARY_STAT_FLAT) {
        return { type: target.effectType, statId: target.statId, amount };
    }
    if (target.effectType === TEMPORARY_ATTRIBUTE_FLAT) {
        return {
            type: target.effectType,
            attributeKey: target.attributeKey,
            amount,
        };
    }
    return { type: target.effectType, amount };
}

export function describeConsumableUseEffects(
    healed,
    satietyRestored,
    hasTimedEffects,
) {
    const effects = [];
    if (Number.isFinite(healed) && healed > 0) {
        effects.push(`Recovered ${healed} HP`);
    }
    if (Number.isFinite(satietyRestored) && satietyRestored > 0) {
        effects.push(
            `Restored ${Number(satietyRestored.toFixed(2))}% satiety`,
        );
    }
    if (hasTimedEffects) {
        effects.push('Temporary effects queued');
    }
    return effects;
}

export function formatConsumableDuration(seconds) {
    const numeric = Number(seconds);
    if (!Number.isFinite(numeric) || numeric <= 0) {
        return '';
    }
    if (numeric % 3600 === 0) {
        const hours = numeric / 3600;
        return `${formatNumber(hours)} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    if (numeric % 60 === 0) {
        const minutes = numeric / 60;
        return `${formatNumber(minutes)} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }
    return `${formatNumber(numeric)} ${numeric === 1 ? 'second' : 'seconds'}`;
}

export function formatConsumableEffect(effect, targets) {
    const type = getConsumableEffectType(effect);
    const amount = formatNumber(read(effect, 'amount', 'Amount'));
    if (type === RESTORE_HEALTH_FLAT) {
        return `Restores ${amount} HP instantly`;
    }
    if (type === RESTORE_SATIETY_FLAT) {
        return `Restores ${amount}% satiety`;
    }

    const target = findConsumableEffectTarget(effect, targets);
    const displayName = target?.displayName
        || read(effect, 'attributeKey', 'AttributeKey')
        || read(effect, 'statId', 'StatId')
        || 'Unknown effect';
    const rawUnit = String(target?.unit || '');
    const unit = rawUnit.trim();

    if (unit === '%') {
        return `+${amount}% ${displayName}`;
    }
    const unitSuffix = unit
        ? (rawUnit.startsWith(' ') ? rawUnit : ` ${unit}`)
        : '';
    return `+${amount}${unitSuffix} ${displayName}`;
}

export function validateConsumableEffects(item, targets) {
    const effects = getConsumableEffects(item);
    const supportedTargets = new Set((targets || []).map(target => target.value));
    const seenTargets = new Set();
    const errors = [];

    effects.forEach((effect, index) => {
        const effectNumber = index + 1;
        const targetValue = getConsumableTargetValue(effect);
        const type = getConsumableEffectType(effect);
        const amount = Number(read(effect, 'amount', 'Amount'));

        if (!supportedTargets.has(targetValue)) {
            errors.push({
                field: `target-${index}`,
                message: `Effect ${effectNumber} must use a supported target.`,
            });
        }
        if (seenTargets.has(targetValue)) {
            errors.push({
                field: `target-${index}`,
                message: `Effect ${effectNumber} duplicates an existing target.`,
            });
        }
        seenTargets.add(targetValue);

        if (!Number.isFinite(amount) || amount <= 0) {
            errors.push({
                field: `amount-${index}`,
                message: `Effect ${effectNumber} amount must be greater than zero.`,
            });
        } else if (type === TEMPORARY_ATTRIBUTE_FLAT && !Number.isInteger(amount)) {
            errors.push({
                field: `amount-${index}`,
                message: `Effect ${effectNumber} attribute amount must be a whole number.`,
            });
        }
    });

    if (effects.some(isTimedConsumableEffect)) {
        const duration = Number(getConsumableDurationSeconds(item));
        if (!Number.isInteger(duration) || duration <= 0) {
            errors.push({
                field: 'duration',
                message: 'Timed effects require a positive duration.',
            });
        }
    }

    return errors;
}

export function normalizeConsumableItemForSubmit(item) {
    const effects = getConsumableEffects(item).map((effect) => {
        const type = getConsumableEffectType(effect);
        const amount = Number(read(effect, 'amount', 'Amount'));
        if (type === TEMPORARY_STAT_FLAT) {
            return {
                type,
                statId: read(effect, 'statId', 'StatId'),
                amount,
            };
        }
        if (type === TEMPORARY_ATTRIBUTE_FLAT) {
            return {
                type,
                attributeKey: read(effect, 'attributeKey', 'AttributeKey'),
                amount,
            };
        }
        return { type, amount };
    });
    const hasTimedEffects = effects.some(isTimedConsumableEffect);
    const {
        ConsumableDurationSeconds: _pascalDuration,
        ConsumableEffects: _pascalEffects,
        ...normalized
    } = item;

    return {
        ...normalized,
        consumableDurationSeconds: hasTimedEffects
            ? Number(getConsumableDurationSeconds(item))
            : null,
        consumableEffects: effects,
    };
}
